import { useEffect, useRef } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './RotateKeyDialog.css';

export interface RotateKeyDialogProps {
  open: boolean;
  keyName: string;
  isRotating: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export function RotateKeyDialog({
  open,
  keyName,
  isRotating,
  onConfirm,
  onCancel,
}: RotateKeyDialogProps): JSX.Element | null {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
  }, [open]);

  if (!open) return null;

  const titleId = 'rotate-key-dialog-title';
  const descId = 'rotate-key-dialog-desc';

  return (
    <DialogElement
      ref={dialogRef}
      className="rotate-key-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="dialog-content dialog-content--flow">
        <div className="rotate-key-dialog-header">
          <forge-icon name="autorenew" className="rotate-key-icon" aria-hidden="true"></forge-icon>
          <h2 id={titleId} className="forge-typography--headline5">
            Rotate API Key
          </h2>
        </div>

        <div id={descId} className="rotate-key-dialog-body">
          <p className="forge-typography--body1">
            You are rotating the key <strong>{keyName}</strong>.
          </p>
          <p className="forge-typography--body2 rotate-key-overlap-note">
            Both the old and new key will be active for a 1-hour overlap window. Update your
            integrations with the new key before the overlap expires.
          </p>
        </div>

        <div className="dialog-actions dialog-actions--borderless">
          <ForgeButton variant="text" onClick={onCancel} disabled={isRotating} type="button">
            Cancel
          </ForgeButton>
          <ForgeButton
            variant="raised"
            onClick={onConfirm}
            disabled={isRotating}
            type="button"
            data-testid="confirm-rotate-button"
          >
            {isRotating ? 'Rotating...' : 'Rotate key'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
